import { ref, computed } from 'vue'
import type { Ref } from 'vue'

// Hook that drives the in-app update UX. Wraps useRegisterSW (from
// vite-plugin-pwa) and adds:
//   - currentVersion / latestVersion exposed to the UI
//   - a manual "check for updates" action with a spinner-friendly state
//   - explicit state machine instead of two loose refs
//
// Callers pass `packageVersion` (from their own package.json) and the
// PWA register-SW hook so this stays framework-agnostic.

export type AppUpdateState =
  | 'idle'
  | 'checking'
  | 'up-to-date'
  | 'update-available'

export interface UseAppUpdateOptions {
  packageVersion: string
  // Pass the `useRegisterSW(...)` result from your app's main entry —
  // we deliberately don't import vite-plugin-pwa here so this composable
  // remains importable from anywhere (and is testable).
  needRefresh: Ref<boolean>
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>
  // Optional: pass a callback that registers an SW-update hook so we
  // can call registration.update() manually. If omitted, manual check
  // falls back to just refetching /version.json.
  registerOnSWHook?: (
    callback: (registration: ServiceWorkerRegistration | undefined) => void,
  ) => void
}

const VERSION_ENDPOINT = '/version.json'

const fetchLatestVersion = async (): Promise<string | null> => {
  try {
    const res = await fetch(VERSION_ENDPOINT, { cache: 'no-store' })
    if (!res.ok) return null
    const data = (await res.json()) as { version?: string }

    return typeof data.version === 'string' ? data.version : null
  } catch {
    return null
  }
}

export default function useAppUpdate(options: UseAppUpdateOptions) {
  const state = ref<AppUpdateState>('idle')
  const latestVersion = ref<string | null>(null)
  const swRegistration = ref<ServiceWorkerRegistration | null>(null)

  if (options.registerOnSWHook) {
    options.registerOnSWHook((registration) => {
      swRegistration.value = registration || null
    })
  }

  // When the SW signals a waiting update, fetch the new version metadata
  // and flip into update-available. We use needRefresh as the source of
  // truth because it's what vite-plugin-pwa toggles for us.
  const resolveOnUpdateAvailable = async () => {
    latestVersion.value = await fetchLatestVersion()
    state.value = 'update-available'
  }

  // Manual user-driven check. Triggers a SW update poll + version refetch.
  const checkForUpdate = async () => {
    state.value = 'checking'
    try {
      if (swRegistration.value?.update) {
        await swRegistration.value.update()
      }
      // After update() resolves the SW may have flipped needRefresh
      // (async). Give it a moment, then settle state.
      await new Promise((resolve) => setTimeout(resolve, 300))

      if (options.needRefresh.value) {
        await resolveOnUpdateAvailable()
      } else {
        state.value = 'up-to-date'
      }
    } catch {
      state.value = 'up-to-date'
    }
  }

  const applyUpdate = () => options.updateServiceWorker()

  // React to needRefresh becoming true (driven by SW lifecycle) even
  // when the user hasn't pressed Check.
  const watchSWUpdate = () => {
    if (options.needRefresh.value && state.value !== 'update-available') {
      void resolveOnUpdateAvailable()
    }
  }

  const isChecking = computed(() => state.value === 'checking')
  const isUpdateAvailable = computed(() => state.value === 'update-available')

  return {
    state,
    isChecking,
    isUpdateAvailable,
    currentVersion: options.packageVersion,
    latestVersion,
    checkForUpdate,
    applyUpdate,
    watchSWUpdate,
  }
}
